import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { HrefResolverService, ServicePathProviderService } from '@blk/cwp-ng-framework';
import { PortfolioRetrievalParamsConfig, PortfolioType } from '@ddt-web/config';
import { ConfigService, InjectConfig, SharedConfigKey } from '@ddt-web/config-layer';
import { ApiParamPosition, PortfolioRetrievalParams, UserPerms, WebUserDataConstants } from '@ddt-web/domain';
import { PartnerQueryParamKey } from '@ddt-web/url';
import { PartnerConstants } from '@ddt-web/user-data';
import { Observable } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';

import { PreferenceChildData, PreferenceData, UpdatePreferenceResponse } from './models/preference.interface';
import { MergeConfig } from './models/wds/request/merge-config.interface';
import { PasRcResolverService } from './pas-rc-resolver.service';
@Injectable({
	providedIn: 'root'
})
export class ApiService {
	private static readonly PORTFOLIO_ANALYSIS: string = '/wds/services/portfolio-analysis';
	private static readonly PARTNER_DATA_URL: string = '/wds/partner-data/';
	private static readonly API_GATEWAY: string = 'api-gateway';

	// AC Server API Calls
	protected static readonly EXTERNAL_ACS_GET_PORTFOLIO_URL: string = '/advisor-center/portfolio/';
	private static readonly EXTERNAL_ACS_PARENT_LOOKUP_URL: string = '/advisor-center/portfolio/parents/';

	@InjectConfig(SharedConfigKey.AWE_USE_ALADDINID) private readonly useAladdinId: boolean;

	constructor(
		protected http: HttpClient,
		private servicePathProvider: ServicePathProviderService,
		private hrefResolverService: HrefResolverService,
		private configService: ConfigService,
		private pasRcResolver: PasRcResolverService
	) { }

	protected getApiGatewayPath(path: string): string {
		return this.servicePathProvider.getServicePath(ApiService.API_GATEWAY) + path;
	}

	public fetchPortfolioAnalysis(rcs: string, params?: any): Observable<any> {
		const url: string = this.getApiGatewayPath(ApiService.PORTFOLIO_ANALYSIS + '?rcs=' + rcs) + this.getExtraQueryParams([PartnerQueryParamKey.TEST_PARTNER_FIRM], '&');
		return this.http.post(url, params);
	}

	public fetchACSPortfolioData(portfolioIds: string[], apiParamOptions: PortfolioRetrievalParams = PortfolioRetrievalParamsConfig.DEFAULT): Observable<any> {
		// TODO chmalcol: Ideally would prefer to use the annotation but it didn't work but good to look into in the future!
		// External portfolios need to have their prefix removed to leave behind uniqueId
		const finalIds = portfolioIds.map(id => id.includes(PartnerConstants.PARTNER.EXT_IDENTIFIER) ? id.split('@').pop() : id);
		const useWweSecurityEnricher = this.configService.get<boolean>(SharedConfigKey.USE_WWE_SECURITY_ENRICHER, false);
		const url: string = this.getApiGatewayPath(ApiService.EXTERNAL_ACS_GET_PORTFOLIO_URL) + finalIds + this.getExtraQueryParams([PartnerQueryParamKey.TEST_PARTNER_FIRM]);

		return this.pasRcResolver.getPortfolioRetrievalParams().pipe(
			map((apiOptions) => (
				Object.assign(
					{},
					apiParamOptions,
					{
						useWweSecurityEnricher: useWweSecurityEnricher,
						securityDetailsRc: apiOptions?.securityDetailsRc,
						usePasModels: apiOptions?.usePasModels,
						modelsRc: apiOptions?.modelsRc
					},
					this.useAladdinId ? { useAladdinId: true } : {}
				)
			)),
			switchMap((params: PortfolioRetrievalParams) => this.http.post(url, params)),
			map((response: any) => {
				if (response?.success) {
					response?.resultList.forEach(portfolio => {
						portfolio.children = this.removeModelHoldingsWithZeroWeight(portfolio.children, portfolio.type);
						portfolio.children = this.combineDuplicateHoldings(portfolio.children);
					});
				}
				return response;
			})
		);
	}

	/**
	 * Find out in which and how many portfolios a holding is contained
	 * Used to find out how many blend portfolios are impacted when editing a portfolio. Only looks 1 level up.
	 */
	public fetchPortfoliosContainingHolding(id: string): Observable<any> {
		const url: string = this.getApiGatewayPath(WebUserDataConstants.USER_DATA_URL.SEARCH);
		const params: any = {
			filters: [
				{ field: 'type', value: 'PORTFOLIO_POSITION' },
				{ field: 'identifier', value: id }
			],
			useCache: false
		};
		return this.http.post(url, params);
	}

	/**
	 * Find out in how many levels we are able to add, based on how many levels of parents the current tree structure goes up to.
	 */
	public fetchAncestors(ids: Array<string>): Observable<any> {
		const url: string = this.getApiGatewayPath(ApiService.EXTERNAL_ACS_PARENT_LOOKUP_URL);
		return this.http.get(url + ids);
	}

	public fetchUserData(action: string, params?: any, rcs?: string): Observable<any> {
		let url: string = this.getApiGatewayPath(ApiService.PARTNER_DATA_URL + action);
		if (rcs) {
			url = url + '?rcs=' + rcs;
		}
		return this.http.post(url, params);
	}

	public fetchManagedModelsConfig(): Observable<any> {
		const url: string = this.getApiGatewayPath(WebUserDataConstants.MANAGED_MODELS_CONFIG);
		return this.http.get(url);
	}

	public fetchThirdPartyModelsList(): Observable<any> {
		const url: string = this.getApiGatewayPath(WebUserDataConstants.THIRD_PARTY_MODELS_LIST) + this.getExtraQueryParams([PartnerQueryParamKey.TEST_PARTNER_FIRM]);
		return this.http.get(url);
	}

	public fetchAllModels(): Observable<any> {
		const apiVersion = WebUserDataConstants.ALL_MODELS_LIST_V2;
		const url: string = this.getApiGatewayPath(apiVersion) + this.getExtraQueryParams([PartnerQueryParamKey.TEST_PARTNER_FIRM]);
		return this.http.get(url);
	}

	private getExtraQueryParams(queryParamsToAdd: string[], prefix: '?' | '&' = '?'): string {
		const currentQueryParams = new URLSearchParams(window.location.search);
		const resolvedQueryParams = queryParamsToAdd.map(queryParam => {
			if (currentQueryParams.has(queryParam)) {
				// Encode key and value so delimiter characters (& = #) in caller-supplied values cannot split/inject query params.
				return encodeURIComponent(queryParam) + '=' + encodeURIComponent(currentQueryParams.get(queryParam));
			}
			return null;
		})
			.filter(queryParam => !!queryParam)
			.join('&');

		return resolvedQueryParams ? `${prefix}${resolvedQueryParams}` : '';
	}

	/**
	 * Fetches security data info for the provided identifiers and with the provided rcs.
	 * Identifiers should be comma separated per security, built as identifierType:identifier.
	 * eg of identifiers: 'SECURITY_ID:IVV@464287200@US4642872000,SECURITY_ID:AGG@464287226@US4642872265'
	 * 
	 * @param identifiers identifiers used to get security data info
	 * @param rcs rcs which contains the response fields received as part of the response
	 */
	public fetchSecurityData(identifiers: string | Array<string>, rcs: string): Observable<any> {
		const url: string = this.getApiGatewayPath(WebUserDataConstants.SECURITY_DATA);
		return this.http.post(url, { rc: rcs, identifiers });
	}

	public fetchGlsDocuments(docIds?: Array<string>, keyword?: string): Observable<any> {
		const url: any = this.hrefResolverService.getResolvedUrl('glsSearch');
		const params = {};
		if (docIds) {
			Object.assign(params, {
				documentId: docIds.toString()
			});
		}
		if (keyword) {
			Object.assign(params, {
				keyword: keyword
			});
		}
		return this.http.get(url, {
			params: params
		});
	}

	// TODO: fix this one as well
	public fetchSecurityTaxesWithPositions(positions: Array<ApiParamPosition> = [], portfolioId: string, isPartner: boolean = false): Observable<any> {
		const url: string = this.getApiGatewayPath(WebUserDataConstants.TAXES) + '?rcs=bfa-tax';

		if (isPartner) {
			return this.fetchSecurityTaxesForPartner(positions, url);
		} else {
			const params: any = {
				portfolios: {
					id: portfolioId,
					positions: positions
				}
			};

			return this.http.post(url, params);
		}

	}

	public fetchSecurityTaxesForPartner(positions: Array<ApiParamPosition>, url: string): Observable<any> {
		return this.http.post(url, {
			identifiers: JSON.stringify(positions)
		});
	}

	public setPreference(preferenceKey: string, preferenceData: any, removeDynamicFieldSuffix: boolean = true): Observable<any> {
		const url = this.getApiGatewayPath(WebUserDataConstants.USER_DATA_URL.SET);
		return this.http.post(url, {
			data: preferenceData,
			key: preferenceKey,
			removeDynamicFieldSuffix: removeDynamicFieldSuffix
		});
	}

	public editPreference(preferenceData: Array<PreferenceChildData>, mergeConfig: MergeConfig): Observable<UpdatePreferenceResponse> {
		const url = this.getApiGatewayPath(WebUserDataConstants.USER_DATA_URL.EDIT);
		return this.http.post<UpdatePreferenceResponse>(url, {
			datas: preferenceData,
			mergeConfig
		});
	}

	public removePreference(preferenceIds: string[]): Observable<UpdatePreferenceResponse> {
		const url = this.getApiGatewayPath(WebUserDataConstants.USER_DATA_URL.REMOVE);
		return this.http.post<UpdatePreferenceResponse>(url, {
			uniqueIds: preferenceIds
		});
	}

	public addPreference(preferenceData: PreferenceData): Observable<UpdatePreferenceResponse> {
		const url = this.getApiGatewayPath(WebUserDataConstants.USER_DATA_URL.ADD);
		const body = { data: preferenceData, addPerms: WebUserDataConstants.USER_DATA_PERMS };
		return this.http.post<UpdatePreferenceResponse>(url, body);
	}

	public addPreferencesForSharing(
		sharingData: {
			addPerms: {
				[UserPerms.VIEW]: string[];
				[UserPerms.EDIT]: string[];
				[UserPerms.DELETE]: string[];
			};
			data: any;
		}): Observable<UpdatePreferenceResponse> {
		const url = this.getApiGatewayPath(WebUserDataConstants.USER_DATA_URL.ADD);
		return this.http.post<UpdatePreferenceResponse>(url, sharingData);
	}

	public fetchPreference(preferenceKey: string, removeDynamicFieldSuffix: boolean = true): Observable<PreferenceData> {
		const url = this.getApiGatewayPath(WebUserDataConstants.USER_DATA_URL.GET_PREFERENCE);
		return this.http.post<PreferenceData>(url, {
			keys: preferenceKey,
			removeDynamicFieldSuffix: removeDynamicFieldSuffix
		});
	}

	public savePortfolio(url: string, params: any): Observable<any> {
		return this.http.post(this.servicePathProvider.getServicePath('api-gateway') + url, params) as Observable<any>;
	}

	private combineDuplicateHoldings(holdings: any[]): any[] {
		return Object.values(holdings.reduce((acc, holding) => {
			const identifier = holding?.aladdinId ?? holding.identifier;
			// To avoid modifying original holdings!
			const updatedHolding = {
				...holding
			};

			const weight = holding.weight ?? holding.allData?.weight;
			if (!!weight) {
				updatedHolding.weight = (acc?.[identifier]?.weight ?? 0) + weight;
				if (updatedHolding.allData) {
					updatedHolding.allData.weight = updatedHolding.weight;
				}
			}

			const value = holding.value ?? holding.allData?.value;
			if (!!value) {
				updatedHolding.value = (acc?.[identifier]?.value ?? 0) + value;
				if (updatedHolding.allData) {
					updatedHolding.allData.value = updatedHolding.value;
				}

			}
			acc[identifier] = updatedHolding;
			return acc;
		}, {}));
	}

	// remove holdings with zero weight from any Model Portfolio or Third Party Model Portfolio
	// it should also remove holdings with zero weight for a blend with model
	private removeModelHoldingsWithZeroWeight(holdings: any[], type: string): any[] {
		if (type === PortfolioType.MODEL || type === PortfolioType.THIRD_PARTY) {
			return holdings.filter(holding => {
				return holding?.weight !== 0 && holding?.allData?.weight !== 0 && holding?.value !== 0 && holding?.allData?.value !== 0;
			});
		} else { //blend portfolio with a model use case
			return holdings.map(holding => {
				if (holding?.portfolio?.children) {
					holding.portfolio.children = this.removeModelHoldingsWithZeroWeight(holding.portfolio.children, holding.portfolio.type);
				}
				return holding;
			});
		}
	}

}

